import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Table from '../../../components/Table'
import Button from '../../../components/Button'
import Loader from '../../../components/Loader'
import Toast from '../../../components/Toast/Toast'
import { handleOpenModal } from '../../../utils'
import { HUTRIT_PRIMARY_COLOR } from '../../../utils/constants'
import { PRIVATE_ROUTES } from '../../../Routes/routes'
import { deleteCourse, getCoursesList } from '../../../services/Courses'
import { TAGS } from '../../../services/Tags'
import CourseFormModal, { COURSE_FORM_MODAL_ID } from './components/CourseFormModal'

const StatusBadge = ({ active }) => (
  <span
    className="badge"
    style={{
      background: active ? '#dcfce7' : '#fee2e2',
      color: active ? '#166534' : '#991b1b',
    }}
  >
    {active ? 'Activo' : 'Inactivo'}
  </span>
)

const Courses = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const MySwal = withReactContent(Swal)
  const [selectedCourse, setSelectedCourse] = useState(null)

  const { data: courses = [], isLoading } = useQuery(
    [TAGS.courses_list],
    getCoursesList,
    {
      select: (data) => {
        const list = data?.res?.courses
        return Array.isArray(list) ? list : []
      },
    }
  )

  const { mutate: removeCourse } = useMutation(deleteCourse, {
    onSuccess: ({ response, res }) => {
      if (!response?.ok) {
        Toast({
          icon: 'error',
          title: res?.msg || res?.error || 'No se pudo eliminar el curso',
        })
        return
      }
      queryClient.invalidateQueries([TAGS.courses_list])
      Toast({ title: 'Curso eliminado correctamente' })
    },
    onError: () =>
      Toast({ icon: 'error', title: 'No se pudo eliminar el curso' }),
  })

  const handleDelete = (course) => {
    MySwal.fire({
      title: <strong>¿Deseas eliminar este curso?</strong>,
      text: 'Se eliminarán también sus videos. Esta acción no se puede deshacer.',
      icon: 'warning',
      confirmButtonColor: HUTRIT_PRIMARY_COLOR,
      confirmButtonText: 'Sí, eliminar',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    }).then(({ isConfirmed }) => {
      if (isConfirmed) removeCourse({ courseId: course.id })
    })
  }

  const openCreate = () => {
    setSelectedCourse(null)
    handleOpenModal({ id: COURSE_FORM_MODAL_ID })
  }

  const openEdit = (course) => {
    setSelectedCourse(course)
    handleOpenModal({ id: COURSE_FORM_MODAL_ID })
  }

  const goToClasses = (course) =>
    navigate(
      PRIVATE_ROUTES.master_course_detail.replace(':id', String(course.id))
    )

  const HeadComponent = () => (
    <tr className="text-on-bg-primary">
      <th scope="col">Título</th>
      <th scope="col">Descripción</th>
      <th scope="col">Esencial</th>
      <th scope="col">Visitas de talentos</th>
      <th scope="col">Clases vistas</th>
      <th scope="col">Estado</th>
      <th scope="col" className="text-center">
        Acciones
      </th>
    </tr>
  )

  const BodyComponent = () =>
    courses.map((course) => (
      <tr key={course.id}>
        <td
          className="fw-bold"
          style={{
            maxWidth: 320,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={course.title}
        >
          {course.title}
        </td>
        <td
          style={{
            maxWidth: 320,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={course.description}
        >
          {course.description}
        </td>
        <td>{course.essential ? 'Sí' : 'No'}</td>
        <td>{course.viewsPublic ?? 0}</td>
        <td>{course.viewsTalents ?? 0}</td>
        <td>
          <StatusBadge active={course.active} />
        </td>
        <td>
          <div className="d-flex justify-content-center gap-2 flex-wrap">
            <Button
              variant="secondary"
              isSmall
              onClick={() => openEdit(course)}
            >
              Editar
            </Button>
            <Button isSmall onClick={() => goToClasses(course)}>
              Gestionar clases
            </Button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ borderRadius: '8px' }}
              onClick={() => handleDelete(course)}
            >
              Eliminar
            </button>
          </div>
        </td>
      </tr>
    ))

  return (
    <DashboardWrapper showTitle title="Cursos">
      <CourseFormModal
        course={selectedCourse}
        onClose={() => setSelectedCourse(null)}
      />

      <div className="d-flex justify-content-end align-items-center mb-3 gap-2 flex-wrap">
        <Button
          variant="secondary"
          onClick={() => navigate(PRIVATE_ROUTES.master_exam_tracking)}
        >
          Seguimiento de exámenes
        </Button>
        <Button onClick={openCreate}>Crear curso</Button>
      </div>

      {isLoading ? (
        <Loader />
      ) : courses.length === 0 ? (
        <p className="text-on-bg-secondary mb-0">No hay cursos registrados.</p>
      ) : (
        <Table BodyComponent={BodyComponent} HeadComponent={HeadComponent} />
      )}
    </DashboardWrapper>
  )
}

export default Courses
